import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MementoLogo, SalonUpLogo } from "@/components/Brand";

const SALON_IMG = "https://images.unsplash.com/photo-1600948836101-f9ffda59d250?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxoYWlyJTIwc2Fsb24lMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODU1MDM5OTB8MA&ixlib=rb-4.1.0&q=85";

function GoogleG({ className = "h-4 w-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.75-6-6.15S8.7 5.9 12 5.9c1.9 0 3.15.8 3.88 1.5l2.65-2.55C16.83 3.25 14.6 2.25 12 2.25 6.87 2.25 2.7 6.42 2.7 11.55S6.87 20.85 12 20.85c6.94 0 9.3-4.87 9.3-7.4 0-.5-.05-.9-.13-1.25H12z"/>
    </svg>
  );
}

export default function Register() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { register, loginWithGoogle, verifyRegisterOtp } = useAuth();
  const [form, setForm] = useState({ salon_name: "", name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpCode, setOtpCode] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await register(form);
      if (r?.otp_required) {
        setOtpStep(true);
        toast.success(t("auth.codeSentToEmail"));
      } else {
        toast.success(t("auth.accountCreated"));
        nav("/app");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.registerError"));
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyRegisterOtp(form.email, otpCode);
      toast.success(t("auth.registrationComplete"));
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.invalidCode"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[color:var(--sp-bg)]">
      <div className="relative flex items-center justify-center p-8 lg:p-16">
        <div className="absolute inset-0 memento-soft-glow pointer-events-none" />
        <div className="w-full max-w-md sp-anim relative">
          <MementoLogo withTagline size="lg" />

          <span className="label-eyebrow block mt-10">{t("auth.registerEyebrow")}</span>
          <h1 className="font-display text-4xl font-black mt-3 mb-2 tracking-tight"><Trans i18nKey="auth.registerTitle" components={[<span className="text-gradient" key="0" />]} /></h1>
          <p className="text-[color:var(--sp-text-secondary)] mb-8">{t("auth.registerSubtitle")}</p>

          <button data-testid="register-google-btn" type="button" onClick={loginWithGoogle} className="sp-btn-google">
            <GoogleG /> {t("auth.continueWithGoogle")}
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-[color:var(--sp-border)]" />
            <span className="text-xs text-[color:var(--sp-text-muted)] uppercase tracking-widest">{t("common.or")}</span>
            <div className="flex-1 h-px bg-[color:var(--sp-border)]" />
          </div>

          {!otpStep ? (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-bold">{t("auth.salonName")}</label>
                <input data-testid="register-salon-input" required className="sp-input mt-1.5" placeholder={t("auth.salonNamePlaceholder")} value={form.salon_name} onChange={set("salon_name")} />
              </div>
              <div>
                <label className="text-sm font-bold">{t("auth.yourName")}</label>
                <input data-testid="register-name-input" required className="sp-input mt-1.5" placeholder={t("auth.yourNamePlaceholder")} value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label className="text-sm font-bold">{t("auth.email")}</label>
                <input data-testid="register-email-input" type="email" required className="sp-input mt-1.5" placeholder={t("auth.emailPlaceholder")} value={form.email} onChange={set("email")} />
              </div>
              <div>
                <label className="text-sm font-bold">{t("auth.password")}</label>
                <input data-testid="register-password-input" type="password" required minLength={8} className="sp-input mt-1.5" placeholder={t("auth.passwordPlaceholder")} value={form.password} onChange={set("password")} />
                <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">
                  {t("auth.passwordRule")}
                </div>
              </div>
              <button data-testid="register-submit-button" disabled={loading} className="sp-btn-primary w-full mt-2">
                {loading ? t("auth.creating") : t("auth.createMySalon")}
              </button>
            </form>
          ) : (
            <form onSubmit={submitOtp} className="space-y-4" data-testid="register-otp-form">
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
                <Trans i18nKey="auth.registerOtpHint" values={{ email: form.email }} components={[<b key="0" />]} />
              </div>
              <div>
                <label className="text-sm font-bold">{t("auth.verificationCode")}</label>
                <input data-testid="register-otp-input" required inputMode="numeric" maxLength={6}
                       pattern="[0-9]{6}"
                       className="sp-input mt-1.5 tracking-[0.4em] text-center text-xl font-mono"
                       placeholder="000000" value={otpCode}
                       onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))} />
              </div>
              <button data-testid="register-otp-verify-btn" disabled={loading} className="sp-btn-primary w-full">
                {loading ? t("auth.verifying") : t("auth.verifyAndCreate")}
              </button>
            </form>
          )}

          <p className="text-sm text-[color:var(--sp-text-secondary)] mt-8">
            {t("auth.alreadyRegistered")}{" "}
            <Link data-testid="register-login-link" to="/login" className="text-[color:var(--sp-brand)] font-bold hover:underline">{t("auth.goToLogin")}</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img src={SALON_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,14,26,0.25) 0%, rgba(11,14,26,0.7) 100%)" }} />
        <div className="relative h-full flex items-end p-14">
          <div>
            <SalonUpLogo className="h-10 mb-8" white />
            <p className="font-display text-3xl font-black leading-tight tracking-tight text-white max-w-md">
              Non insegniamo a <span className="text-gradient">lavorare di più</span>. Insegniamo a costruire un&apos;azienda che lavori meglio.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
